import { attributionService } from 'config/api';
import { getAttributionQuery, isUrlValid, trimWebProductId } from 'utils';

export const getTemplateAttributes = (options) => {
  const { userBrand, sharedProduct, sharedLook, config, isConciergeCartTemplate = false } = options || {};
  // evaluates based on expression result

  switch (true) {
    case !!sharedProduct:
      return {
        TemplateId: config.get(`commsHub.${userBrand}.templates.generic`),
      };
    case !!sharedLook:
      return {
        TemplateId: config.get(`commsHub.${userBrand}.templates.generic`),
      };
    case isConciergeCartTemplate:
      return {
        TemplateId: config.get(`commsHub.${userBrand}.templates.shareConciergeCart`),
      };

    default:
      return {};
  }
};

export const addAttributionParams = (sharedProduct, userBrand, associatePin) => ({
  ...sharedProduct,
  sharedProductLink: `${sharedProduct.sharedProductLink}${getAttributionQuery(associatePin, userBrand)}`,
});

export const attachSignature = (message, signature, removeSignatureCenterAlignment) => {
  let signatureTemplateString = signature;
  if (removeSignatureCenterAlignment) {
    // for case when we send message without template (text only)
    signatureTemplateString = signature.replace('margin: 10px auto', 'margin: 10px 10px');
  }
  return `${message}${signatureTemplateString}`;
};

export const generateEditSubjectLine = ({ editCategory, editTitle }) => {
  switch (editCategory) {
    case 'PRODUCT_SPOTLIGHT':
      return 'Product Spotlight!';
    case 'REFRESH_EDITS':
      return 'The Refresh!';
    default:
      return editTitle;
  }
};

export const getPreparedConciergeProducts = async ({
  products,
  productDetailsService,
  isOrderAttributionEnabled,
  userBrand,
  associateId,
  config,
}) => {
  // exit if conciergeCartData is empty
  if (!products?.length) return;

  const sharedProducts = products.map(async (item) => {
    const { name, brand, color, size, imageUrl, quantity, price, canonicalUrl, webProductSku, itemskuId } = item;
    const webProduct = webProductSku.split('/');
    let link = null;
    let _canonicalUrl = canonicalUrl;
    // fetch product details for getting the canonicalUrl
    if (!_canonicalUrl) {
      try {
        const productId = webProduct[0];
        const {
          data: { details },
        } = await productDetailsService.get(`v3/productdetails/${productId}`);
        _canonicalUrl = details?.canonicalUrl;
      } catch (err) {
        console.error('Products details fetch:', err); /* eslint-disable-line */
        return null;
      }
    }
    const baseUrl = config.get(`links.${userBrand.toUpperCase()}`);
    link = `${baseUrl}${_canonicalUrl}`;
    if (isOrderAttributionEnabled) {
      link = `${link}${getAttributionQuery(associateId, userBrand)}`;
    }

    const adjustedPromoPrice = price?.adjustedPromoPrice ? price?.adjustedPromoPrice.toFixed(2) : null;
    const actualPrice = adjustedPromoPrice || price?.promoPrice || price?.adjustedPrice || price?.currentPrice;

    return {
      price: actualPrice ? `$${actualPrice}` : null,
      imageUrl: isUrlValid(imageUrl) ? imageUrl : null,
      link,
      name,
      brand,
      color,
      size,
      quantity,
      pimSkuId: itemskuId,
      webProductId: webProduct[0],
      webSkuId: webProduct[1],
    };
  });

  return Promise.all(sharedProducts);
};

const getRecipientsEmails = (recipients) => recipients.map(({ contact }) => contact);

const getAssetsIds = (assets) => assets.map(({ item: { assetId } }) => assetId);

const getLookDetails = (attachments) =>
  attachments
    .filter(({ item: { productId } }) => !!productId)
    .map((attachment) => {
      const {
        color,
        designerName,
        name,
        pimSkuId,
        previewUrl,
        price,
        productId,
        productUrl,
        size,
        skuId,
        webProductId,
      } = attachment.item;
      return {
        color,
        brand: designerName,
        name,
        pimSkuId,
        imgUrl: previewUrl,
        price,
        productId,
        link: productUrl,
        size,
        skuId,
        webProductId,
      };
    });

/**
 * @typedef ClientTagsData
 * @param sharedLook
 * @param assets
 * @param clientId
 * @param conciergeCartSharing
 * @param conciergeProducts
 *
 * @param associateId
 * @param recipients
 * @param {ClientTagsData} clientData
 */
export const generateTags = ({ associateId, recipients, templateAttributes }) => {
  const {
    sharedProduct,
    sharedLook,
    assets,
    clientId,
    conciergeProducts,
    conciergeCartSharing,
    connectLookShare,
    attachments,
    shareSource,
    isEditShareTrackingEnbaled,
  } = templateAttributes;

  const tags = {
    associatePin: associateId,
    recipientEmails: getRecipientsEmails(recipients),
  };

  if (sharedProduct) {
    if (shareSource) {
      tags.shareSource = shareSource;
    }
    tags.productId = sharedProduct.productId;
    tags.pimSkuId = sharedProduct.pimSkuId;
    tags.skuId = sharedProduct.skuId;
  } else if (sharedLook) {
    if (connectLookShare) {
      tags.shareSource = 'clientConnectDetail';
    }
    tags.lookId = sharedLook.id;

    if (isEditShareTrackingEnbaled && sharedLook?.frame) {
      delete tags.lookId;
      tags.editId = sharedLook.id;
      tags.editFrameType = sharedLook.frame;
    }
  }

  if (assets?.length) {
    tags.assetsIds = getAssetsIds(assets);
  }

  if (conciergeCartSharing) {
    tags.conciergeProducts = conciergeProducts;
    tags.cartId = `${clientId}|Concierge Cart`;
  }

  if (attachments) {
    tags.looksProducts = getLookDetails(attachments);
  }

  return tags;
};

export const getTemplateType = ({ sharedLook, sharedProduct, conciergeCartData }) => {
  if (sharedLook) {
    return '/email-look-template';
  }

  if (sharedProduct) {
    return '/product-share';
  }

  if (conciergeCartData) {
    return '/concierge-cart';
  }

  return '/standard-email';
};

export const exclusiveInviteScript = `Hi, exciting news! You’re on the exclusive invite list to Stanley—our new Neiman Marcus app! You’ll be one of the first to use it. \n
Experience a daily feed of personalized recommendations in INSPIRE, looks created for you in STYLE, and services you can browse and book in SERVE. You can also share a look with me and message me right from the app!  \n
The link to the app store is below so you can download it. Just use your account information from NeimanMarcus.com to create your account or sign in. Try sending me a message once you’re in so we can check out the app together! \n
https://apps.apple.com/us/app/stanley-by-neiman-marcus/id1571660387`;

export const generateAttributionItemsForEmail = (parameters) => {
  const {
    Tags: { pimSkuId, productId, skuId, looksProducts, conciergeProducts },
  } = parameters;
  // Look and Product Share
  if (looksProducts?.length) {
    return looksProducts.map(({ pimSkuId, productId, skuId, webProductId }) => ({
      pim_sku_id: pimSkuId,
      web_product_id: trimWebProductId(webProductId || productId),
      sku_id: skuId,
    }));
  }
  // concierge cart share
  if (conciergeProducts?.length) {
    return conciergeProducts.map((conciergeProduct) => ({
      pim_sku_id: conciergeProduct?.pimSkuId,
      web_product_id: trimWebProductId(conciergeProduct?.webProductId || productId),
      sku_id: conciergeProduct?.webSkuId,
    }));
  }

  return [
    {
      pim_sku_id: pimSkuId,
      web_product_id: productId,
      sku_id: skuId,
    },
  ];
};

export const generateEmailAttributionParameters = ({ parameters, attributionRecipientsEmails }) => ({
  items: generateAttributionItemsForEmail(parameters),
  customer_ids: attributionRecipientsEmails, // array of emails
  associate_pin: parameters?.InternalEntityId, // associate_pin
  destination: 'NMO',
  date_shared: Math.floor(Date.now() / 1000),
});

export const saveAttributionShared = async ({ formattedContact, associatePin, pimSkuIds, productIds, skuIds }) => {
  await attributionService.post('/products', {
    customer_ids: [formattedContact],
    associate_pin: associatePin,
    destination: 'BGO',
    date_shared: Math.floor(Date.now() / 1000),
    items: pimSkuIds.map((pimId, index) => {
      return {
        pim_sku_id: pimId,
        web_product_id: productIds[index],
        sku_id: skuIds[index],
      };
    }),
  });
};
